import { Box, Button, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { Dispatch, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dateResponseMap } from '../../helpers';
import {
  changeCausesFilterCause,
  changeCausesFilterCountry,
  changeCausesFilterDate,
  changeCausesFilterStore,
  findMissingClosingCollectionsCausesRequested,
  findMissingClosingSalesCausesRequested,
  reportClosingFilterViewModeSelector,
  reportClosingSalesCausesSelector,
  reportMissingClosingCollectionsCausesSelector,
} from '../../state';
import { ClosingViewMode } from '../../types';
import { ErrorHeader } from './ErrorHeader';
import { CollectionErrors } from './CollectionErrors';
import { SaleErrors } from './SaleErrors';
import routes from '../../config/routes';
import DialogFlexSales from '../DialogFlexSales';
import { MissingDialogParams } from './types';

const resolveParams = (
  params: MissingDialogParams | undefined,
  searchParams: URLSearchParams
): MissingDialogParams | null => {
  if (params) return params;

  const country = searchParams.get('country');
  const store = searchParams.get('store');
  const date = dateResponseMap(searchParams.get('date')) as Date;
  const cause = searchParams.get('cause');

  if (country && store && date && cause) {
    return { country, store, date, cause };
  }

  return null;
};

const handleParams = (
  dispatch: Dispatch<AnyAction>,
  params: MissingDialogParams,
  salesRequested: boolean,
  collectionsRequested: boolean
) => {
  dispatch(changeCausesFilterCountry(params.country));
  dispatch(changeCausesFilterStore(params.store));
  dispatch(changeCausesFilterDate(params.date));
  dispatch(changeCausesFilterCause(params.cause));
  if (salesRequested) dispatch(findMissingClosingSalesCausesRequested());
  if (collectionsRequested) dispatch(findMissingClosingCollectionsCausesRequested());
};

interface MissingsDialogProps {
  params?: MissingDialogParams;
  onClose?: () => void;
}

interface CauseDetailSection {
  key: string;
  order: number;
  hasContent: boolean;
  content: ReactNode;
}

const DetailsDialog = ({ params, onClose }: MissingsDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const viewMode = useSelector(reportClosingFilterViewModeSelector);
  const salesResource = useSelector(reportClosingSalesCausesSelector);
  const collectionsResource = useSelector(reportMissingClosingCollectionsCausesSelector);
  const showSaleErrors = [ClosingViewMode.CONSOLIDATED, ClosingViewMode.SALES].includes(viewMode);
  const showCollectionErrors = [ClosingViewMode.CONSOLIDATED, ClosingViewMode.COLLECTIONS].includes(
    viewMode
  );

  const handleCancel = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    navigate(routes.protectedNestedRoutes.missingClosingDetail.index.path);
  }, [navigate, onClose]);

  const resolvedParams = useMemo(() => resolveParams(params, searchParams), [params, searchParams]);

  const orderedCauseSections = useMemo(() => {
    const sections: CauseDetailSection[] = [];

    if (showSaleErrors) {
      sections.push({
        key: 'sales',
        order: 0,
        hasContent: (salesResource.data?.details?.length || 0) > 0,
        content: <SaleErrors />,
      });
    }

    if (showCollectionErrors) {
      sections.push({
        key: 'collections',
        order: 1,
        hasContent: (collectionsResource.data?.details?.length || 0) > 0,
        content: <CollectionErrors />,
      });
    }

    return sections.sort((a, b) => {
      if (a.hasContent === b.hasContent) return a.order - b.order;
      return a.hasContent ? -1 : 1;
    });
  }, [
    showSaleErrors,
    showCollectionErrors,
    salesResource.data?.details?.length,
    collectionsResource.data?.details?.length,
  ]);

  useEffect(() => {
    if (resolvedParams) {
      handleParams(dispatch, resolvedParams, showSaleErrors, showCollectionErrors);
    }
  }, [showCollectionErrors, dispatch, showSaleErrors, resolvedParams, viewMode]);

  return (
    <DialogFlexSales
      onClose={handleCancel}
      open
      scroll="paper"
      maxWidth="md"
      PaperProps={{
        sx: {
          width: 'min(1200px, calc(100vw - 32px))',
          maxWidth: 'unset',
          height: 'calc(100vh - 32px)',
          maxHeight: 'calc(100vh - 32px)',
        },
      }}
    >
      <DialogTitle>{t('missing-closing-causes.title')}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ flexShrink: 0 }}>
            <ErrorHeader />
          </Box>
          {orderedCauseSections.map(section => (
            <Box key={section.key} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {section.content}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('navigation.buttons.close')}</Button>
      </DialogActions>
    </DialogFlexSales>
  );
};

const MissingsDialog = (props: MissingsDialogProps) => <DetailsDialog {...props} />;

export default MissingsDialog;
